import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';

import { capabilitiesOf, useHarnesses } from '@/features/harnesses';
import type { Harness } from '@/features/harnesses';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';

import { EFFORT_LABELS, EFFORT_OPTIONS } from '../effortLabels';
import { isLlmProfileError } from '../errors';
import type { LlmProfileErrorField } from '../errors';
import { convertModelForPrefix, diagnoseStoredModel } from '../openAiModelId';
import type { LlmProfile, LlmProfileEffort } from '../types';
import { validateModel, validateName } from '../validation';
import { ClaudeModelCombobox } from './ClaudeModelCombobox';
import { NoHarnessesEmptyState } from './NoHarnessesEmptyState';
import { OpenAiModelCombobox } from './OpenAiModelCombobox';

export type LlmProfileFormMode =
  { kind: 'create' } | { kind: 'edit'; entry: LlmProfile };

/** Suffixed onto a non-selectable harness's option label (spec 136 FR-006). */
const STATUS_LABEL: Record<Harness['connection_status'], string> = {
  untested: 'Untested',
  failed: 'Failed',
  ok: 'Connected',
};

export interface LlmProfileFormValues {
  name: string;
  model: string;
  effort: LlmProfileEffort;
  max_turns: number | null;
  description: string | null;
  ai_provider_id: string;
}

/**
 * Add a profile, or edit an existing one.
 *
 * The Harness selector comes first — it determines the model catalog and
 * the agent CLI the profile runs through — then name, model, effort, and
 * max turns. No base_url, credential, or workspace field anywhere
 * (FR-012), and no test-connection action (FR-014). When no harness
 * exists yet, submission is blocked and `NoHarnessesEmptyState` points
 * the admin at the Harnesses section instead (FR-013).
 *
 * Mirrors `LlmProfileFormSheet`'s (pre-refactor) load-bearing decisions:
 * remount per target, dismissal blocked while a save is in flight, a failed
 * submit keeps the sheet open with the input intact.
 */
export function LlmProfileFormSheet({
  mode,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  mode: LlmProfileFormMode;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (input: LlmProfileFormValues) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;

  const { entries: harnesses, isPending: harnessesPending } = useHarnesses();
  const availableHarnesses = harnesses ?? [];
  // A profile has no credentials of its own — only `ai_provider_id` — so a
  // harness whose connection is unverified or broken fails silently until a
  // workflow execution runs it (spec 136). The profile's own already-saved
  // harness is the one exception: unrelated edits must not be blocked just
  // because that harness's connection degraded after the profile was set up.
  const isHarnessSelectable = (harness: Harness) =>
    harness.connection_status === 'ok' || harness.id === entry?.ai_provider_id;
  const noHarnessesAvailable =
    !harnessesPending &&
    availableHarnesses.filter(isHarnessSelectable).length === 0;

  const [model, setModel] = useState(entry?.model ?? '');
  const [name, setName] = useState(entry?.name ?? '');
  const [effort, setEffort] = useState<LlmProfileEffort>(
    entry?.effort ?? EFFORT_OPTIONS[0],
  );
  const [maxTurns, setMaxTurns] = useState(
    entry?.max_turns != null ? String(entry.max_turns) : '',
  );
  const [description, setDescription] = useState(entry?.description ?? '');
  const [harnessId, setHarnessId] = useState<string>(
    entry?.ai_provider_id ??
      availableHarnesses.find((harness) => harness.connection_status === 'ok')
        ?.id ??
      '',
  );
  const [submitted, setSubmitted] = useState(false);
  // Seeding the harness selector is the form's doing, not the admin's; only
  // a choice they make counts as a change.
  const [harnessChosen, setHarnessChosen] = useState(false);

  const selectedHarness = availableHarnesses.find(
    (harness) => harness.id === harnessId,
  );
  const harnessType = selectedHarness?.harness_type;
  const capabilities = capabilitiesOf(harnessType);
  // What the harness's gateway puts in front of every OpenAI model id — read from
  // the backend, never derived from the base URL here (spec 205 FR-003).
  const modelIdPrefix = selectedHarness?.model_id_prefix;

  // Why the model field just changed or needs a second look after a harness switch
  // (spec 205 FR-005). Cleared as soon as the admin edits the field themselves.
  // Bumped when the form itself rewrites the model (a harness switch converting it,
  // the "Use <id>" shortcut) so the picker remounts and shows the new value; the
  // picker only seeds its text from `value` on mount.
  const [modelRevision, setModelRevision] = useState(0);

  const [modelNotice, setModelNotice] = useState<{
    tone: 'info' | 'warning';
    text: string;
  } | null>(null);

  // The backend's verdict on a profile that is already stored (spec 205 US3). Shown
  // only while the field still holds the stored value; once changed it has done its job.
  const storedNotice =
    entry && model === entry.model
      ? diagnoseStoredModel(entry.model_status)
      : null;

  // Which model catalog, and which agent CLI the profile will run through —
  // the two things choosing a harness here actually decides. The wording of the
  // CLI is the harness's own label, never a table here (spec 240 FR-004).
  const agentLabel = selectedHarness?.label;
  const harnessHint = agentLabel
    ? `Sets the model list below to match this harness. ${agentLabel}.`
    : 'Sets the model list below to match this harness, and the agent CLI the profile runs through.';

  // `useHarnesses()` resolves after this component's initial render — the
  // state initializer above only ever sees an empty list on mount. Once the
  // real list arrives, seed the still-unset selector with its first verified
  // entry (never overriding a value the admin, or an edited entry, already
  // has) — never an unverified/failed one, matching the Select's own gate
  // (spec 136 FR-001/FR-002).
  useEffect(() => {
    if (harnessId !== '') return;
    const firstOkHarnessId = availableHarnesses.find(
      (harness) => harness.connection_status === 'ok',
    )?.id;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- seeding a controlled selector once its async data source resolves, not deriving render state
    if (firstOkHarnessId) setHarnessId(firstOkHarnessId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the list arriving, not to harnessId being cleared by user choice
  }, [availableHarnesses]);

  const modelId = useId();
  const modelErrorId = useId();
  const nameId = useId();
  const nameErrorId = useId();
  const effortId = useId();
  const effortErrorId = useId();
  const maxTurnsId = useId();
  const maxTurnsErrorId = useId();
  const descriptionId = useId();
  const descriptionHelpId = useId();
  const descriptionErrorId = useId();
  const harnessFieldId = useId();
  const harnessErrorId = useId();

  const serverError = isLlmProfileError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;

  const localNameError = validateName(name);
  const localModelError = validateModel(model);

  const trimmedMaxTurns = maxTurns.trim();
  const parsedMaxTurns =
    trimmedMaxTurns === '' ? null : Number.parseInt(trimmedMaxTurns, 10);
  const maxTurnsError =
    trimmedMaxTurns !== '' &&
    (parsedMaxTurns === null ||
      Number.isNaN(parsedMaxTurns) ||
      parsedMaxTurns < 1 ||
      String(parsedMaxTurns) !== trimmedMaxTurns)
      ? 'Enter a whole number of at least 1, or leave it blank.'
      : null;

  function messageFor(field: LlmProfileErrorField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (field === 'form') {
      if (serverError?.field === 'slug') return serverError.message;
      return genericError;
    }
    if (field === 'name')
      return submitted ? (localNameError ?? undefined) : undefined;
    if (field === 'model')
      return submitted ? (localModelError ?? undefined) : undefined;
    if (field === 'max_turns') return maxTurnsError ?? undefined;
    return undefined;
  }

  /** Structural emptiness, the unparsed max-turns state, and a chosen harness (FR-013). */
  const canSubmit =
    name.trim().length > 0 &&
    model.trim().length > 0 &&
    maxTurnsError === null &&
    harnessId !== '' &&
    !noHarnessesAvailable &&
    !isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    onSubmit({
      name,
      model,
      effort,
      max_turns: parsedMaxTurns,
      // Blank is none (spec 168); the service trims and checks the rest.
      description: description.trim() === '' ? null : description.trim(),
      ai_provider_id: harnessId,
    });
  }

  const dirty =
    name !== (entry?.name ?? '') ||
    model !== (entry?.model ?? '') ||
    effort !== (entry?.effort ?? EFFORT_OPTIONS[0]) ||
    maxTurns !== (entry?.max_turns != null ? String(entry.max_turns) : '') ||
    description !== (entry?.description ?? '') ||
    (editing ? harnessId !== entry?.ai_provider_id : harnessChosen);
  // A successful save closes through `open`, so it never asks.
  const { requestClose, dialog } = useDiscardGuard(dirty, onCancel);

  const modelMessage = messageFor('model');
  const nameMessage = messageFor('name');
  const effortMessage = messageFor('effort');
  const maxTurnsMessage = messageFor('max_turns');
  const descriptionMessage = messageFor('description');
  const harnessMessage = messageFor('ai_provider_id');
  const formMessage = messageFor('form');

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Edit ${entry?.name}` : 'Add a profile'}
            </SheetTitle>
            <SheetDescription>
              A profile names a model and the settings a workflow step runs it
              under.
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={harnessFieldId}>Harness</Label>
                  <RequiredMark />
                </div>
                {noHarnessesAvailable ? (
                  <NoHarnessesEmptyState />
                ) : (
                  <Select
                    value={harnessId}
                    onValueChange={(next) => {
                      if (!next) return;
                      const nextType = availableHarnesses.find(
                        (harness) => harness.id === next,
                      )?.harness_type;
                      // Switching to a harness of a different type (per
                      // spec 160 Clarifications) clears the model field
                      // immediately — a model id from one harness's
                      // catalog is almost never valid for the other's.
                      if (nextType !== undefined && nextType !== harnessType) {
                        setModel('');
                        setModelNotice(null);
                      } else if (
                        capabilitiesOf(nextType).modelSource ===
                          'openai-catalog' &&
                        model.trim() !== ''
                      ) {
                        // Same type, possibly a different gateway: the ids this
                        // harness accepts may carry a different prefix. Convert a
                        // catalog id and say so; keep anything else and warn — never
                        // submit a mismatched id without telling the admin.
                        const nextPrefix = availableHarnesses.find(
                          (harness) => harness.id === next,
                        )?.model_id_prefix;
                        if ((nextPrefix ?? '') !== (modelIdPrefix ?? '')) {
                          const converted = convertModelForPrefix(
                            model,
                            modelIdPrefix,
                            nextPrefix,
                          );
                          if (converted !== null) {
                            setModel(converted);
                            setModelRevision((revision) => revision + 1);
                            setModelNotice({
                              tone: 'info',
                              text: `Model changed to ${converted} to match this harness.`,
                            });
                          } else {
                            setModelNotice({
                              tone: 'warning',
                              text: `"${model}" may not be valid for this harness. Pick a model from the list.`,
                            });
                          }
                        }
                      }
                      setHarnessId(next);
                      setHarnessChosen(true);
                      if (serverError?.field === 'ai_provider_id')
                        onClearError();
                    }}
                    disabled={isPending || harnessesPending}
                  >
                    <SelectTrigger
                      id={harnessFieldId}
                      aria-label="Harness"
                      aria-invalid={harnessMessage ? true : undefined}
                      aria-describedby={
                        harnessMessage ? harnessErrorId : undefined
                      }
                    >
                      <SelectValue>
                        {(value: string) =>
                          availableHarnesses.find(
                            (harness) => harness.id === value,
                          )?.name ?? 'Choose a harness'
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableHarnesses.map((harnessOption) => {
                        const selectable = isHarnessSelectable(harnessOption);
                        return (
                          <SelectItem
                            key={harnessOption.id}
                            value={harnessOption.id}
                            disabled={!selectable}
                          >
                            {selectable
                              ? harnessOption.name
                              : `${harnessOption.name} — ${STATUS_LABEL[harnessOption.connection_status]}`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                {harnessMessage ? (
                  <p id={harnessErrorId} className="text-destructive text-xs">
                    {harnessMessage}
                  </p>
                ) : !noHarnessesAvailable ? (
                  <p className="text-muted-foreground text-xs">{harnessHint}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={nameId}>Name</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={nameId}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (serverError?.field === 'name') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="Implementation model"
                  autoFocus
                  aria-invalid={nameMessage ? true : undefined}
                  aria-describedby={nameMessage ? nameErrorId : undefined}
                />
                {nameMessage ? (
                  <p id={nameErrorId} className="text-destructive text-xs">
                    {nameMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={modelId}>Model</Label>
                  <RequiredMark />
                </div>
                {capabilities.modelSource === 'openai-catalog' ? (
                  <OpenAiModelCombobox
                    key={modelRevision}
                    id={modelId}
                    idPrefix={modelIdPrefix}
                    value={model}
                    onChange={(next) => {
                      setModel(next);
                      setModelNotice(null);
                      if (serverError?.field === 'model') onClearError();
                    }}
                    disabled={isPending}
                    aria-invalid={modelMessage ? true : undefined}
                    aria-describedby={modelMessage ? modelErrorId : undefined}
                  />
                ) : (
                  <ClaudeModelCombobox
                    id={modelId}
                    value={model}
                    onChange={(next) => {
                      setModel(next);
                      if (serverError?.field === 'model') onClearError();
                    }}
                    disabled={isPending}
                    aria-invalid={modelMessage ? true : undefined}
                    aria-describedby={modelMessage ? modelErrorId : undefined}
                  />
                )}
                {modelMessage ? (
                  <p id={modelErrorId} className="text-destructive text-xs">
                    {modelMessage}
                  </p>
                ) : null}
                {modelNotice ? (
                  <p
                    role="status"
                    className={
                      modelNotice.tone === 'warning'
                        ? 'text-destructive text-xs'
                        : 'text-muted-foreground text-xs'
                    }
                  >
                    {modelNotice.text}
                  </p>
                ) : null}
                {storedNotice ? (
                  <p role="status" className="text-destructive text-xs">
                    {storedNotice.kind === 'use-id' ? (
                      <>
                        This model is not valid for this harness. Use{' '}
                        <button
                          type="button"
                          className="font-mono underline underline-offset-2"
                          onClick={() => {
                            setModel(storedNotice.id);
                            setModelRevision((revision) => revision + 1);
                          }}
                        >
                          {storedNotice.id}
                        </button>
                        .
                      </>
                    ) : storedNotice.kind === 'removed' ? (
                      'This model is no longer available. Choose another model.'
                    ) : (
                      'This model is not valid for this harness. Choose another model.'
                    )}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={effortId}>Effort</Label>
                  <RequiredMark />
                </div>
                <Select
                  value={effort}
                  onValueChange={(next) => {
                    if (!next) return;
                    setEffort(next as LlmProfileEffort);
                    if (serverError?.field === 'effort') onClearError();
                  }}
                  disabled={isPending}
                >
                  <SelectTrigger
                    id={effortId}
                    aria-label="Effort"
                    aria-invalid={effortMessage ? true : undefined}
                    aria-describedby={effortMessage ? effortErrorId : undefined}
                  >
                    <SelectValue>
                      {(value: string) =>
                        EFFORT_LABELS[value as LlmProfileEffort]
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {EFFORT_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {EFFORT_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {effortMessage ? (
                  <p id={effortErrorId} className="text-destructive text-xs">
                    {effortMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={maxTurnsId}>
                  Max turns{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id={maxTurnsId}
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={maxTurns}
                  onChange={(event) => {
                    setMaxTurns(event.target.value);
                    if (serverError?.field === 'max_turns') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="No override"
                  aria-invalid={maxTurnsMessage ? true : undefined}
                  aria-describedby={
                    maxTurnsMessage ? maxTurnsErrorId : undefined
                  }
                />
                {maxTurnsMessage ? (
                  <p id={maxTurnsErrorId} className="text-destructive text-xs">
                    {maxTurnsMessage}
                  </p>
                ) : null}
              </div>

              {/* What the profile is for, in the admin's words: everyone who
                  chooses or reads a step sees it (spec 214). */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={descriptionId}>
                  Description{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id={descriptionId}
                  value={description}
                  maxLength={200}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    if (serverError?.field === 'description') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="What this profile is for"
                  aria-invalid={descriptionMessage ? true : undefined}
                  aria-describedby={
                    descriptionMessage
                      ? `${descriptionHelpId} ${descriptionErrorId}`
                      : descriptionHelpId
                  }
                />
                <p
                  id={descriptionHelpId}
                  className="text-muted-foreground text-xs"
                >
                  One line, up to 200 characters. Shown to everyone who chooses
                  this profile.
                </p>
                {descriptionMessage ? (
                  <p
                    id={descriptionErrorId}
                    className="text-destructive text-xs"
                  >
                    {descriptionMessage}
                  </p>
                ) : null}
              </div>

              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={!canSubmit}>
                {isPending ? 'Saving…' : editing ? 'Save' : 'Add'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {dialog}
    </>
  );
}
